"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";
import {
  disableClientModeAction,
  enableAccountClientModeAction,
  enablePresetClientModeAction,
  listClientModeAccountsAction,
} from "@/lib/actions/client-mode";
import {
  CLIENT_PERMISSION_PRESETS,
  type ClientPermissionPreset,
} from "@/lib/auth/client-permissions";
import type {
  ClientModeAccountOption,
  ClientModePreset,
  ResolvedClientMode,
} from "@/lib/auth/client-mode-shared";

const PRESET_OPTIONS = Object.entries(CLIENT_PERMISSION_PRESETS) as Array<[
  Exclude<ClientPermissionPreset, "custom">,
  { label: string; description: string },
]>;

export function ClientModeToggle({
  mode,
  variant = "topbar",
}: {
  mode: ResolvedClientMode | null;
  variant?: "topbar" | "drawer";
}) {
  const { demoMode, setDemoMode } = useClientSidebar();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<ClientModeAccountOption[] | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drawer = variant === "drawer";

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function openMenu() {
    setOpen((value) => !value);
    if (open || accounts !== null) return;
    setError(null);
    try {
      setAccounts(await listClientModeAccountsAction());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load client accounts.");
      setAccounts([]);
    }
  }

  function prepareTransition() {
    setBusy(true);
    setError(null);
    if (demoMode) setDemoMode(false);
  }

  async function choosePreset(preset: ClientModePreset) {
    prepareTransition();
    try {
      const result = await enablePresetClientModeAction(preset);
      window.location.assign(result.destination);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : "Unable to enable client mode.");
    }
  }

  async function chooseAccount(subjectUserId: string) {
    prepareTransition();
    try {
      const result = await enableAccountClientModeAction(subjectUserId);
      window.location.assign(result.destination);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : "Unable to enable client mode.");
    }
  }

  async function returnToStaff() {
    setBusy(true);
    setError(null);
    try {
      const result = await disableClientModeAction();
      window.location.assign(result.destination);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : "Unable to restore staff permissions.");
    }
  }

  const normalizedQuery = query.trim().toLowerCase();
  const filteredAccounts = useMemo(
    () => (accounts ?? []).filter((account) =>
      `${account.name ?? ""} ${account.email}`.toLowerCase().includes(normalizedQuery),
    ),
    [accounts, normalizedQuery],
  );

  return (
    <div ref={containerRef} className={`relative flex gap-1 ${drawer ? "col-span-2 flex-col items-stretch" : "items-center"}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        disabled={busy}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`flex items-center gap-1.5 rounded-md font-semibold transition-colors disabled:opacity-60 ${
          drawer ? "min-h-11 w-full px-3 text-sm" : "h-8 max-w-52 px-2 text-xs"
        } ${
          mode
            ? "bg-spark-yellow/[.16] text-spark-yellow hover:bg-spark-yellow/[.24]"
            : "text-[#AAB2BD] hover:bg-white/[.06] hover:text-white"
        }`}
        title={mode ? `Client mode: ${mode.label}` : "Choose client permissions to preview"}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M19 8v6" />
          <path d="M22 11h-6" />
        </svg>
        <span className="truncate">{mode ? `Client: ${mode.label}` : "Client mode"}</span>
      </button>

      {mode && (
        <button
          type="button"
          onClick={returnToStaff}
          disabled={busy}
          className={drawer
            ? "min-h-11 w-full rounded-md px-3 text-left text-sm font-semibold text-spark-yellow transition-colors hover:bg-spark-yellow/[.15] disabled:opacity-60"
            : "h-8 rounded-md px-2 text-xs font-semibold text-spark-yellow transition-colors hover:bg-spark-yellow/[.15] disabled:opacity-60"
          }
          title="Return to staff permissions"
        >
          Return to Staff
        </button>
      )}

      {open && (
        <div role="menu" className={`z-[120] flex max-h-[34rem] flex-col overflow-hidden rounded-lg border border-spark-light-gray bg-white text-spark-charcoal shadow-[var(--shadow-card-hover)] ${
          drawer ? "fixed inset-x-3 top-16" : "absolute right-0 top-full mt-2 w-96"
        }`}>
          <div className="border-b border-spark-light-gray p-3">
            <p className="text-sm font-bold text-spark-black">Client mode</p>
            <p className="mt-0.5 text-xs text-spark-gray">
              Preview client permissions while actions remain attributed to you.
            </p>
          </div>

          <div className="overflow-y-auto p-2">
            {mode && (
              <button
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={returnToStaff}
                className="mb-2 w-full rounded-md bg-spark-green px-3 py-2 text-left text-sm font-bold text-spark-ink hover:brightness-95 disabled:opacity-60"
              >
                Use staff permissions
              </button>
            )}

            <p className="px-1 pb-1 pt-1 text-[11px] font-bold uppercase tracking-[.07em] text-spark-gray">
              Templates · all companies
            </p>
            <div className="space-y-1">
              {PRESET_OPTIONS.map(([preset, option]) => {
                const selected = mode?.kind === "preset" && mode.preset === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    disabled={busy || selected}
                    onClick={() => choosePreset(preset)}
                    className={`w-full rounded-md px-3 py-2 text-left transition-colors disabled:opacity-60 ${
                      selected ? "bg-spark-blue-tint" : "hover:bg-spark-hover"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-spark-black">{option.label}</span>
                    <span className="block text-xs text-spark-gray">{option.description}</span>
                  </button>
                );
              })}
            </div>

            <div className="my-2 border-t border-spark-light-gray" />
            <label htmlFor="client-mode-account-search" className="px-1 text-[11px] font-bold uppercase tracking-[.07em] text-spark-gray">
              Existing client account
            </label>
            <input
              id="client-mode-account-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name or email…"
              className="my-2 w-full rounded-md border border-spark-border px-3 py-2 text-sm outline-none focus:border-spark-blue focus:ring-2 focus:ring-spark-blue/20"
            />
            {accounts === null && <p className="px-3 py-2 text-xs text-spark-gray">Loading accounts…</p>}
            <div className="space-y-0.5">
              {filteredAccounts.map((account) => {
                const selected = mode?.kind === "account" && mode.subjectUserId === account.id;
                return (
                  <button
                    key={account.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    disabled={busy || selected}
                    onClick={() => chooseAccount(account.id)}
                    className={`w-full rounded-md px-3 py-2 text-left transition-colors disabled:opacity-60 ${
                      selected ? "bg-spark-blue-tint" : "hover:bg-spark-hover"
                    }`}
                  >
                    <span className="block truncate text-sm font-semibold text-spark-black">{account.name?.trim() || account.email}</span>
                    <span className="block truncate text-xs text-spark-gray">
                      {account.email} · {account.companyCount} {account.companyCount === 1 ? "company" : "companies"}
                    </span>
                  </button>
                );
              })}
              {accounts !== null && filteredAccounts.length === 0 && (
                <p className="px-3 py-3 text-center text-xs text-spark-gray">No client accounts found.</p>
              )}
            </div>
          </div>

          {error && <p role="alert" className="border-t border-spark-light-gray bg-spark-red-tint/40 px-3 py-2 text-xs text-spark-red-text">{error}</p>}
        </div>
      )}
    </div>
  );
}
